// FilterList.tsx - Updated version with sorting and filtering
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  CardContent,
  CardDescription,
  CardFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  LoadingSpinner,
  ScrollArea,
  Separator,
  InfiniteScroll,
} from '@ais-ui/ui-core';
import { CreateFilter } from './Mutations/filter/CreateFilter';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Filter,
  Group,
  useGetAllGroups,
  useGetFilters,
} from '@ais-ui/websockets';
import { filterToIcon } from './filterToIcon';
import { cn, isNonEmpty } from '@ais-ui/utils';
import { v4 as uuidv4 } from 'uuid';
import { PiDotsThreeVertical } from 'react-icons/pi';
import { DeleteFilter } from './Mutations/filter/DeleteFilter';
import { EditFilter } from './Mutations/filter/EditFilter';
import { useLocalization } from '@ais-ui/locale-management';
import { NoItemsEmptyStateList } from '../../components/EmptyList';
import { ApplyFilterButton } from './ApplyFilterButton';
import { ApplyOnStartupButton } from './ApplyOnStartupButton';
import { FilterSort } from './FilterSort';
import { FilterFilter } from './FilterFilter';
import { useFilterFilters } from './useFilterFilters';

interface FilterItemProps {
  filter: Filter;
}

const getGroupsFromIds = (filter: Filter, groups: Group[]): Group[] => {
  const filterGroups = filter.groups || [];

  return (
    groups?.filter(
      (group) =>
        filterGroups.includes(group.uuid) || filterGroups.includes(group.name)
    ) || []
  );
};

interface FilterGridItemProps {
  label: string;
  value: string | undefined | null;
}
const FilterGridItem = ({ label, value }: FilterGridItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : 'Unknown'}
      </p>
    </div>
  );
};

const FilterItem = ({ filter }: FilterItemProps) => {
  const { messages } = useLocalization();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [filterGroups, setFilterGroups] = useState<Group[]>([]);
  const { data: groups } = useGetAllGroups();

  const onDelete = () => {
    setShowDeleteDialog(true);
  };

  const onEdit = () => {
    setShowEditDialog(true);
  };

  useEffect(() => {
    if (groups) {
      setFilterGroups(getGroupsFromIds(filter, groups));
    }
  }, [groups, filter]);

  const FilterIcon = useMemo(() => filterToIcon(filter), [filter]);

  return (
    <AccordionItem
      key={filter.uuid}
      value={filter.uuid ? filter.uuid : uuidv4()}
      className={cn('pr-4 pl-2')}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <div className="flex space-x-1 items-center flex-shrink-0">
            <ApplyOnStartupButton filter={filter}></ApplyOnStartupButton>
            <ApplyFilterButton filter={filter}></ApplyFilterButton>
            <Separator orientation="vertical" className="h-6 bg-gray-300" />
          </div>
          <AccordionTrigger className="min-w-0 w-[210px]" hideChevron={true}>
            <div className="flex ml-4 space-x-2 items-center min-w-0">
              <FilterIcon className="h-4 w-4 flex-shrink-0" />
              <p className="text-sm text-left font-medium leading-none group-hover:underline truncate">
                {filter.name}
              </p>
            </div>
          </AccordionTrigger>
        </div>
        <FilterDropDownMenu
          filter={filter}
          onDelete={onDelete}
          onEdit={onEdit}
          className="flex-shrink-0"
        ></FilterDropDownMenu>
      </div>

      <AccordionContent>
        <div className="grid gap-6 px-4 pt-4">
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <FilterGridItem
                label={messages.filtersItemOwnerLabel}
                value={filter.owner}
              />
              <FilterGridItem
                label={messages.filtersItemShareLabel}
                value={
                  filterGroups.length > 0
                    ? filterGroups.map((group) => group.name).join(', ')
                    : messages.filtersItemShareEmptyValue
                }
              />
            </div>
            <FilterGridItem
              label={messages.filtersItemDefinitionLabel}
              value={filter.filter}
            />
          </div>
        </div>
      </AccordionContent>
      {showDeleteDialog && (
        <DeleteFilter
          filter={filter}
          showDialog={showDeleteDialog}
          setShowDialog={setShowDeleteDialog}
        />
      )}
      {showEditDialog && (
        <EditFilter
          showDialog={showEditDialog}
          setShowDialog={setShowEditDialog}
          filter={filter}
        />
      )}
    </AccordionItem>
  );
};

const FilterDropDownMenu = ({
  filter,
  onDelete,
  onEdit,
  className,
}: {
  filter: Filter;
  onDelete: () => void;
  onEdit: (event: React.FormEvent) => void;
  className?: string;
}) => {
  const { messages } = useLocalization();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className={className}>
        <Button
          id={`${filter.name}-dropdown-button`}
          size="icon"
          variant="ghost"
          className="h-8 w-8"
        >
          <PiDotsThreeVertical className="h-6 w-6" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onEdit}>
          {messages.genericEdit}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onDelete}>
          {messages.genericDelete}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const FilterList = () => {
  const { messages } = useLocalization();
  const [search, setSearch] = useState<string>('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const { data: filters, isLoading } = useGetFilters();

  const {
    filteredAndSortedFilters,
    filterFilters,
    setFilterFilters,
    sortConfig,
    setSortConfig,
  } = useFilterFilters(filters || [], search);

  return (
    <>
      {/* Controls section - outside CardContent to avoid overflow-hidden */}
      <div className="px-6 pb-4">
        <CardDescription className="mb-4">
          {messages.filtersSubtitle}
        </CardDescription>
        <div className="flex gap-2">
          <Input
            id="search"
            placeholder={messages.filtersSearchFilters}
            className="h-10 flex-1"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          <FilterSort sortConfig={sortConfig} setSortConfig={setSortConfig} />
          <FilterFilter
            filterFilters={filterFilters}
            setFilterFilters={setFilterFilters}
            filters={filters}
          />
        </div>
      </div>

      {/* Content section */}
      <CardContent className="flex-1 flex flex-col overflow-hidden pt-0">
        {!isLoading && filters?.length === 0 && (
          <NoItemsEmptyStateList entity="filter" srcImage="empty_list_bro.png">
            <Button
              id="filters-create-button"
              onClick={() => setShowCreateDialog(true)}
              className="w-full h-10"
            >
              Create filter
            </Button>
          </NoItemsEmptyStateList>
        )}

        {isLoading && <LoadingSpinner />}

        {!isLoading && filters?.length !== 0 && (
          <Accordion
            className="flex-1 flex flex-col overflow-hidden"
            type="single"
            collapsible
          >
            <InfiniteScroll
              items={filteredAndSortedFilters}
              limit={30}
              observeResize={true}
              className="flex-grow p-0 overflow-y-auto border rounded-lg"
              renderItem={(value: Filter) => {
                return <FilterItem key={value.uuid} filter={value} />;
              }}
            />
          </Accordion>
        )}
      </CardContent>

      <CardFooter className="flex flex-grow-0 flex-col items-end min-h-52">
        {filters?.length !== 0 && (
          <Button
            id="filters-create-button"
            onClick={() => setShowCreateDialog(true)}
          >
            {messages.genericCreate}
          </Button>
        )}

        {showCreateDialog && (
          <CreateFilter
            showDialog={showCreateDialog}
            setShowDialog={setShowCreateDialog}
          />
        )}
      </CardFooter>
    </>
  );
};
